import { getApolloClient } from '@faustwp/core';
import { CaseStudyPage } from '../../components/CaseStudyPage';
import {
  GET_CASE_STUDY_BY_SLUG,
  GET_CASE_STUDY_BY_DATABASE_ID,
  GET_POST_BY_DATABASE_ID,
  GET_POST_BY_DATABASE_ID_MINIMAL,
  GET_POST_BY_SLUG,
  GET_POST_BY_SLUG_AND_CATEGORY,
  GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
  GET_POST_BY_SLUG_AND_LANGUAGE,
  GET_POST_BY_SLUG_MINIMAL,
  GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
  GET_POST_TRANSLATIONS_BY_DATABASE_ID,
} from '../../lib/queries/caseStudyBySlug';
import { ENGLISH_CATEGORY_SLUG, FINNISH_CATEGORY_SLUG } from '../../lib/caseStudyPaths';

function getEnglishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('en')) ?? null;
}

function getFinnishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('fi')) ?? null;
}

export default function WorkCaseStudyPage({ post, generalSettings, fiSlug, canonicalPath }) {
  if (!post) return null;
  return (
    <CaseStudyPage
      post={post}
      generalSettings={generalSettings}
      lang="en"
      fiSlug={fiSlug}
      canonicalPath={canonicalPath}
    />
  );
}

export async function getStaticProps(ctx) {
  const { slug } = ctx.params ?? {};
  if (!slug) {
    return { notFound: true };
  }
  const client = getApolloClient();
  let data = null;
  let post = null;

  try {
    const res = await client.query({
      query: GET_CASE_STUDY_BY_SLUG,
      variables: { slug },
    });
    data = res?.data;
    post = data?.caseStudies?.nodes?.[0] ?? null;
  } catch {
    // Schema may not have caseStudies (not yet migrated)
  }

  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_AND_LANGUAGE,
        variables: { slug },
      });
      data = res?.data;
      post = data?.posts?.nodes?.[0] ?? null;
    } catch {
      // Schema may not have language filter
    }
  }

  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
        variables: { slug },
      });
      data = res?.data;
      const node = data?.posts?.nodes?.[0] ?? null;
      const nodeLang = (node?.language?.code ?? '').toLowerCase();
      const nodeIsEnglish = nodeLang.startsWith('en');
      const enTranslation = node ? getEnglishTranslation(node) : null;
      const idToFetch = nodeIsEnglish ? node?.databaseId : enTranslation?.databaseId;
      if (idToFetch) {
        try {
          const fullRes = await client.query({
            query: GET_POST_BY_DATABASE_ID,
            variables: { databaseId: String(idToFetch) },
          });
          const fullData = fullRes?.data;
          if (fullData?.post) {
            post = fullData.post;
            data = fullData;
          }
        } catch {
          const minimalRes = await client.query({
            query: GET_POST_BY_DATABASE_ID_MINIMAL,
            variables: { databaseId: String(idToFetch) },
          });
          const minimalData = minimalRes?.data;
          if (minimalData?.post) {
            post = minimalData.post;
            data = minimalData;
          }
        }
      }
      if (!post && node) {
        post = enTranslation ?? node;
      }
    } catch {
      // WPGraphQL Polylang not active
    }
  }

  if (!post) {
    try {
      let res = await client.query({
        query: GET_POST_BY_SLUG_AND_CATEGORY,
        variables: { slug, categorySlug: ENGLISH_CATEGORY_SLUG },
      });
      data = res?.data;
      post = data?.posts?.nodes?.[0] ?? null;
      if (!post) {
        res = await client.query({
          query: GET_POST_BY_SLUG_AND_CATEGORY,
          variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
      }
      if (!post) {
        res = await client.query({
          query: GET_POST_BY_SLUG,
          variables: { slug },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
      }
    } catch {
      try {
        let res = await client.query({
          query: GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
          variables: { slug, categorySlug: ENGLISH_CATEGORY_SLUG },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
        if (!post) {
          res = await client.query({
            query: GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
            variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
          });
          data = res?.data;
          post = data?.posts?.nodes?.[0] ?? null;
        }
        if (!post) {
          res = await client.query({
            query: GET_POST_BY_SLUG_MINIMAL,
            variables: { slug },
          });
          data = res?.data;
          post = data?.posts?.nodes?.[0] ?? null;
        }
      } catch {
        // Keep post null; will return notFound
      }
    }
  }

  const generalSettings = data?.generalSettings ?? null;
  if (!post) {
    return { notFound: true };
  }

  let fiSlug = null;
  try {
    let trNode = null;
    try {
      const caseTrRes = await client.query({
        query: GET_CASE_STUDY_BY_DATABASE_ID,
        variables: { databaseId: String(post.databaseId) },
      });
      trNode = caseTrRes?.data?.caseStudy ?? null;
    } catch {
      //
    }
    if (!trNode) {
      const trRes = await client.query({
        query: GET_POST_TRANSLATIONS_BY_DATABASE_ID,
        variables: { databaseId: String(post.databaseId) },
      });
      trNode = trRes?.data?.post ?? null;
    }
    const fiTranslation = trNode ? getFinnishTranslation(trNode) : null;
    fiSlug = fiTranslation?.slug ?? null;
  } catch {
    //
  }

  return {
    props: { post, generalSettings, fiSlug: fiSlug ?? null, canonicalPath: `/work/${slug}` },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}
